import React, { useEffect, useMemo, useRef } from "react";
import SessionContext from "./sessionContext";
import {
  createSessionStore,
  CustomPrompts,
  RagViewSettings,
  SessionActionStatus,
  SessionStore,
  User,
} from "./sessionStore";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { isRtl, SupportedLanguages } from "@kahunmedical/common-configuration";
import { StoreApi } from "zustand";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import {
  CustomFeedbackOption,
  suggestedQuestionOption,
} from "@kahunmedical/common-configuration/dist/configuration/dialogueOptions";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import { summaryToText } from "fe-web-components/patientCard/patientSummary";
import { PatientFirstSummaryFragment } from "fe-common/graphql";

interface SessionProviderProps {
  children: any;
  initialState?: Partial<SessionStore>;
  embedded?: boolean;
  rtlDisplay?: boolean;
  caseId?: string;
  summary?: PatientFirstSummaryFragment | string;
  app?: string;
  showSuggestedQuestions?: suggestedQuestionOption;
  showChatHistory?: boolean;
  user?: User;
  customPrompts?: CustomPrompts;
  viewSettings?: RagViewSettings;
  showFeedbackButtons?: boolean;
  customFeedback?: CustomFeedbackOption[];
}

//This context provider is created in order to initialise the Zustand store with the translation and embedded props.
//see https://docs.pmnd.rs/zustand/guides/initialize-state-with-props#store-creator-with-createstore

const SessionProvider = ({
  initialState,
  embedded,
  rtlDisplay,
  caseId,
  summary,
  app,
  showSuggestedQuestions,
  children,
  showChatHistory,
  user,
  customPrompts,
  viewSettings,
  showFeedbackButtons = true,
  customFeedback,
}: SessionProviderProps) => {
  const storeRef = useRef<StoreApi<SessionStore>>(); //keep zustand store in ref to avoid re-rendering
  const textSummary = useMemo(
    () => (typeof summary !== "object" ? summary : summaryToText(summary)),
    [summary],
  );
  const { translate, locale } = useTranslateByGender();
  const eventReporter = useEventReportedContext();

  const rtl = rtlDisplay || isRtl(locale as SupportedLanguages);

  if (!storeRef.current) {
    console.log("creating session store");
    storeRef.current = createSessionStore(translate, {
      ...initialState,
      ragSettings: {
        ...initialState?.ragSettings,
        rtlDisplay: rtl,
        embeddedView: embedded,
        showChatHistory,
        showFeedbackButtons,
        customFeedback,
      },
      customPrompts,
      viewSettings,
    });
  }

  const generateSuggestions =
    showSuggestedQuestions === suggestedQuestionOption.Always ||
    (showSuggestedQuestions === suggestedQuestionOption["A/B testing"]
      ? Math.random() > 0.5
      : false);
  const {
    ragSettings,
    sessionUser,
    startSessionByCaseId,
    fetchRagSettings,
    setRagSettings,
    setSessionUser,
    setRagDaoHeader,
    resetSession,
  } = storeRef.current?.getState() || {};

  useEffect(() => {
    if (storeRef.current) {
      //fetch session info if not already fetched
      fetchRagSettings().then((settings) => {
        console.log("rag settings:", settings);
      });
    }
  }, [fetchRagSettings]);

  useEffect(() => {
    if (setSessionUser && user) setSessionUser(user);
  }, [user, sessionUser, setSessionUser]);

  useEffect(() => {
    if (storeRef.current && textSummary && caseId) {
      (async () => {
        //start new session or restore an existing session based on caseId
        await startSessionByCaseId(
          textSummary,
          ragSettings.modelVer,
          generateSuggestions,
          caseId,
        ).then((status) => {
          eventReporter?.reportEvent(MIX_PANEL_EVENTS.SUMMARY_SUBMITTED, {
            failed: status !== SessionActionStatus.success,
          });
        });
      })();
    } else if (caseId) {
      resetSession();
    }
  }, [
    textSummary,
    caseId,
    startSessionByCaseId,
    ragSettings,
    generateSuggestions,
    eventReporter,
    resetSession,
  ]);

  useEffect(() => {
    if (setRagSettings) setRagSettings({ rtlDisplay });
  }, [rtlDisplay, setRagSettings]);

  useEffect(() => {
    if (setRagDaoHeader && app) {
      setRagDaoHeader("kahun_rag_app", app);
    }
  }, [app, setRagDaoHeader]);

  //if (!active) return children;

  return (
    <SessionContext.Provider value={storeRef.current}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
